import { Component, OnDestroy } from '@angular/core';

import { DialogService } from 'primeng/dynamicdialog';

import { combineLatest, filter, map, Observable, of, Subject, takeUntil } from 'rxjs';

import { ApiChapterSubjectCategoryListItem, ApiProjectThemaListItem, ApiReplaceSubjectCategoryRequest, ChaptersClient, ProjectDetailsClient, SubjectCategoriesClient, SubjectCategoryType } from 'content-manager/src/app/core/open-api/content-manager-api-client';
import { ProjectStateService } from 'content-manager/src/app/projects/project/project-state.service';
import { IProjectUpdateThemasDialogData } from 'content-manager/src/app/projects/project/themas/update-themas-dialog/project-update-themas-dialog-data';
import { ProjectUpdateThemasDialogComponent } from 'content-manager/src/app/projects/project/themas/update-themas-dialog/project-update-themas-dialog.component';
import { Filters, TableSettings } from 'sc-common';

@Component({
    templateUrl: './project-themas.component.html',
    standalone: false
})
export class ProjectThemasComponent implements OnDestroy {

    public readonly tableModel$: Observable<{ isReadonly: boolean; tableSettings: TableSettings<any>; hasChapters: boolean; }>;

    private readonly _refreshTable$ = new Subject<void>();

    private readonly _destroy$ = new Subject<void>();

    constructor(
        private readonly _projectStateService: ProjectStateService,
        private readonly _subjectCategoriesClient: SubjectCategoriesClient,
        private readonly _chaptersClient: ChaptersClient,
        private readonly _projectsClient: ProjectDetailsClient,
        private readonly _dialogService: DialogService) {

        this.tableModel$ = combineLatest([
            _projectStateService.model$,
            this._projectStateService.switchMap(project => this._chaptersClient.hasChapters(project.id))])
            .pipe(map(([project, hasChapters]) => ({
                isReadonly: project.isReadonly,
                hasChapters: hasChapters,
                tableSettings: hasChapters ? this._initChapterThemasTable() : this._initProjectThemasTable()
            })));

    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        this._refreshTable$.complete();
    }

    // public openProjectThemasDialog(): void {
    //     const dialogData: IProjectUpdateThemasDialogData = {
    //         themas$: this._subjectCategoriesClient.getThemas(),
    //         existingThemasIds$: this._projectStateService.firstSwitchMap(project => this._projectsClient.getThemaIdList(project.id)),
    //         updateAction: themaIds => this._projectStateService.firstSwitchMap(project => this._projectsClient.updateThemas(project.id, themaIds))
    //     };

    //     this._openThemasDialog(dialogData);
    // }

    public openChapterThemasDialog(chapter: ApiChapterSubjectCategoryListItem): void {

        const dialogData: IProjectUpdateThemasDialogData = {
            themas$: this._subjectCategoriesClient.getThemas(),
            existingThemaCodes$: of(chapter.themas.map(t => t.code)),
            updateAction: subjectCategories => this._projectStateService.firstSwitchMap(project =>
                this._chaptersClient.replaceSubjectCategories(
                    project.id,
                    chapter.id,
                    new ApiReplaceSubjectCategoryRequest({ subjectCategoryType: SubjectCategoryType.Thema, subjectCategories })))
        };

        this._openThemasDialog(dialogData);
    }

    private _openThemasDialog(dialogData: IProjectUpdateThemasDialogData): void {

        const dialogRef = this._dialogService.open(ProjectUpdateThemasDialogComponent, {
            width: '35rem',
            header: $localize`Update Themas`,
            closable: true,
            data: dialogData
        });

        dialogRef.onClose
            .pipe(filter(r => r), takeUntil(this._destroy$))
            .subscribe(() => this._refreshTable$.next());
    }

    private _initChapterThemasTable(): TableSettings<ApiChapterSubjectCategoryListItem> {
        const chapterList$ = this._projectStateService.switchMap(project => this._chaptersClient.getAll(project.id));

        return TableSettings.create(ApiChapterSubjectCategoryListItem, {
            dataSource: oDataQuery => this._projectStateService.switchMap(project =>
                this._chaptersClient.getSubjectCategoryList(project.id, SubjectCategoryType.Thema, oDataQuery)),
            columnResizeMode: 'fit',
            actionsCount: 1,
            emptyMessageText: $localize`No chapters`,
            reload$: this._refreshTable$.asObservable(),
            columns: {
                pos: {
                    width: '100px',
                    centered: true
                },
                chapter: {
                    filter: Filters.multiselect(chapterList$.pipe(map(x => x.map(k => ({ id: k.name, name: k.name })))))
                },
                themas: {
                    displayExpr: item => item.code,
                    filterExpr: item => item.code,
                    renderAsCommaSeparatedList: true
                }
            },
            defaultSort: {
                field: x => x.pos
            }
        });
    }

    private _initProjectThemasTable(): TableSettings<ApiProjectThemaListItem> {
        return TableSettings.create(ApiProjectThemaListItem, {
            dataSource: oDataQuery => this._projectStateService.switchMap(project => this._projectsClient.getThemaList(project.id, oDataQuery)),
            columnResizeMode: 'fit',
            emptyMessageText: $localize`No themas`,
            reload$: this._refreshTable$.asObservable()
        });
    }
}
